import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { IoChatbubbleEllipsesOutline, IoSearch, IoPersonCircleOutline, IoChevronBack } from 'react-icons/io5';
import { db } from '../../firebase/config';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { format, isToday, isYesterday } from 'date-fns';

const COLORS = {
  background: '#F4F3F8',
  header: '#2C3640',
  active: '#E85830',
};

const formatTime = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  if (isToday(date)) return format(date, 'a h:mm').replace('AM', '오전').replace('PM', '오후');
  if (isYesterday(date)) return '어제';
  return format(date, 'M/d');
};

const ChatPage = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [focusedRoom, setFocusedRoom] = useState(null);

  const myUid = userData?.uid;

  useEffect(() => {
    if (!myUid) return;

    const q = query(
      collection(db, 'chatRooms'),
      where('participants', 'array-contains', myUid),
      orderBy('lastMessageAt', 'desc')
    );

    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setRooms(list);
      setLoading(false);
    }, (err) => {
      console.error('채팅방 로드 실패:', err);
      setLoading(false);
    });

    return () => unsub();
  }, [myUid]);

  const getRoomDisplayName = (room) => {
    if (room.roomName) return room.roomName;
    const names = room.participantNames || {};
    const otherUid = (room.participants || []).find(uid => uid !== myUid);
    return names[otherUid] || '알 수 없음';
  };

  const getRoomAvatar = (room) => {
    const otherUid = (room.participants || []).find(uid => uid !== myUid);
    const photos = room.participantPhotos || {};
    return photos[otherUid] || null;
  };

  const getUnread = (room) => {
    const counts = room.unreadCount || {};
    return counts[myUid] || 0;
  };

  const filteredRooms = rooms.filter(r => {
    if (!searchQuery) return true;
    const keyword = searchQuery.toLowerCase();
    const name = getRoomDisplayName(r).toLowerCase();
    const msg = (r.lastMessage || '').toLowerCase();
    return name.includes(keyword) || msg.includes(keyword);
  });

  return (
    <Container>
      <Header>
        <BackBtn onClick={() => navigate(-1)}><IoChevronBack size={22} /></BackBtn>
        <HeaderTitle>채팅</HeaderTitle>
        <HeaderRight />
      </Header>

      <RoomList>
        {loading ? (
          <EmptyState>
            <EmptyText>로딩 중...</EmptyText>
          </EmptyState>
        ) : filteredRooms.length === 0 ? (
          <EmptyState>
            <IoChatbubbleEllipsesOutline size={40} color="#ddd" />
            <EmptyText>채팅방이 없습니다</EmptyText>
          </EmptyState>
        ) : (
          filteredRooms.map(room => {
            const unread = getUnread(room);
            return (
              <RoomItem key={room.id} $focused={focusedRoom === room.id} onClick={() => {
                if (focusedRoom === room.id) {
                  navigate(`/chat/${room.id}`);
                } else {
                  setFocusedRoom(room.id);
                }
              }}>
                <Avatar>
                  {getRoomAvatar(room) ? (
                    <AvatarImg src={getRoomAvatar(room)} alt="" />
                  ) : (
                    <IoPersonCircleOutline size={60} color="#B0B8C0" />
                  )}
                </Avatar>
                <RoomInfo>
                  <RoomName>{getRoomDisplayName(room)}</RoomName>
                  <LastMessage>
                    {room.lastMessage || '대화를 시작해보세요'}
                  </LastMessage>
                </RoomInfo>
                <RoomMeta>
                  <RoomTime>{formatTime(room.lastMessageAt)}</RoomTime>
                  {unread > 0 && (
                    <UnreadBadge>{unread > 99 ? '99+' : unread}</UnreadBadge>
                  )}
                </RoomMeta>
              </RoomItem>
            );
          })
        )}
      </RoomList>
    </Container>
  );
};

export default ChatPage;

/* ─── Styled Components ──────────────────── */

const Container = styled.div`
  min-height: 100vh;
  background: #fff;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #F0F0EC;
`;
const BackBtn = styled.button`
  display: flex;
  align-items: center;
  color: #1E2A32;
  padding: 4px;
`;
const HeaderTitle = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #1E2A32;
`;
const HeaderRight = styled.div` width: 32px; `;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 16px 12px;
  padding: 10px 14px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  font-size: 14px;
  outline: none;
  &::placeholder { color: #bbb; }
`;

const RoomList = styled.div`
  padding: 0 16px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 60px 0;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #bbb;
`;

const RoomItem = styled.div`
  display: flex;
  gap: 14px;
  padding: 16px 8px;
  margin: 0 -8px;
  border-bottom: 1px solid #EEEEF0;
  cursor: pointer;
  align-items: center;
  border-radius: 12px;
  background: ${({ $focused }) => $focused ? '#F4F3F8' : 'transparent'};
  transition: background 0.15s;
  &:last-child { border-bottom: none; }
`;

const Avatar = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #F0F0F0;
`;

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const RoomInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
`;

const RoomName = styled.p`
  font-size: 16px;
  font-weight: 700;
  color: #1E2A32;
`;

const LastMessage = styled.p`
  font-size: 13px;
  color: #8C9AA4;
  font-weight: 400;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const RoomMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;
  align-self: flex-start;
  padding-top: 2px;
`;

const RoomTime = styled.span`
  font-size: 12px;
  color: #8C9AA4;
  white-space: nowrap;
`;

const UnreadBadge = styled.span`
  min-width: 22px;
  height: 22px;
  border-radius: 11px;
  background: ${COLORS.active};
  color: white;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
`;
